import { useState, useRef, useEffect, useCallback, type ReactNode, type HTMLAttributes } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';

export interface ScrollableContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  scrollClassName?: string;
  buttonVariant?: 'capsule' | 'chip';
  scrollAmount?: number;
  showArrows?: boolean;
}

export function ScrollableContainer({
  children,
  className,
  scrollClassName,
  buttonVariant = 'chip',
  scrollAmount = 200,
  showArrows = true,
  ...rest
}: ScrollableContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();

    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => checkScroll());
      ro.observe(el);
      Array.from(el.children).forEach((child) => ro?.observe(child));
    }

    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      ro?.disconnect();
    };
  }, [checkScroll, children]);

  const handleScroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = direction === 'left' ? -scrollAmount : scrollAmount;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const isCapsule = buttonVariant === 'capsule';

  const buttonBaseClass = isCapsule
    ? 'h-8 w-7 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-surface-tertiary bg-surface-tertiary/40 shrink-0 transition-colors cursor-pointer border border-border/50 active:scale-95'
    : 'w-8 h-8 rounded-full bg-surface-secondary border border-border flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-tertiary shadow-2xs shrink-0 cursor-pointer transition-all active:scale-95';

  return (
    <div className={clsx('flex items-center gap-1.5 max-w-full w-full', className)} {...rest}>
      {/* Left Navigation Arrow */}
      {showArrows && canScrollLeft && (
        <button
          type="button"
          onClick={() => handleScroll('left')}
          aria-label="Desplazar a la izquierda"
          className={buttonBaseClass}
        >
          <ChevronLeft size={isCapsule ? 14 : 15} strokeWidth={2.5} />
        </button>
      )}

      {/* Scrollable Viewport */}
      <div
        ref={scrollRef}
        className={clsx(
          'flex-1 flex items-center overflow-x-auto hide-scrollbar scroll-smooth min-w-0',
          scrollClassName
        )}
      >
        {children}
      </div>

      {/* Right Navigation Arrow */}
      {showArrows && canScrollRight && (
        <button
          type="button"
          onClick={() => handleScroll('right')}
          aria-label="Desplazar a la derecha"
          className={buttonBaseClass}
        >
          <ChevronRight size={isCapsule ? 14 : 15} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
